import { Box, Flex, chakra } from '@chakra-ui/react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';

import useAdminClickupTableStore from './AdminClickupListTableStore';
import { ClickupTask } from './clickup.types';
import getColumns from './columns';

type Props = {
  data: ClickupTask[];
};

export default function AdminClickupListView({ data }: Props) {
  const { t } = useTranslation();

  const columns = getColumns(data);
  const [
    columnFilters,
    setColumnFilters,
    columnVisibility,
    setColumnVisibility,
    columnSizing,
    setColumnSizing,
  ] = useAdminClickupTableStore((state) => [
    state.columnFilters,
    state.setColumnFilters,
    state.columnVisibility,
    state.setColumnVisibility,
    state.columnSizing,
    state.setColumnSizing,
  ]);
  const [globalFilter, setGlobalFilter] = useState<string>('');

  const table = useReactTable({
    data: data,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      columnSizing,
      columnVisibility,
      columnFilters,
      globalFilter,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columns: columns,
  });

  const filterExtraInfo: FilterExtraInfo<ClickupTask> = [];

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          {t('admin.clickup.title')}
        </chakra.h3>
      </Flex>

      <>
        <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
          <ColumnVisibility table={table} />
          <Filters
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            table={table}
            filterExtraInfo={filterExtraInfo}
          />
          <GlobalFilter
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            maxWidth={{ base: 'none', md: '250px' }}
            flex={{ base: 1, md: 'initial' }}
            minW="150px"
          />
          <ActiveFilters
            columnFilters={columnFilters}
            removeItemFunc={(elem) => {
              setColumnFilters(columnFilters.filter((f) => f.id !== elem.id));
            }}
            filterExtraInfo={filterExtraInfo}
          />
          <Pagination
            table={table}
            pageInfo={{
              hasNextPage: table.getCanNextPage(),
              hasPreviousPage: table.getCanPreviousPage(),
            }}
            totalRows={data.length}
            tablePrefix="clickup"
            ml="auto"
          />
        </Flex>

        <TanstackTableView table={table} />
      </>
    </Box>
  );
}
